import { GRID_TEMPLATE } from "@/routes/app/admin/users/usersTableLayout";
import { Skeleton } from "@/shared/components/ui/Skeleton";
import { cn } from "@/shared/lib/utils";

interface UsersTableSkeletonProps {
  /** Number of placeholder rows to render. */
  rows?: number;
}

/**
 * Loading placeholder for the admin users table. Per design.md §14 it keeps
 * the real container and column header and swaps only the dynamic rows for
 * pulsing glass skeletons, reusing the shared `GRID_TEMPLATE` (and matching
 * `hidden sm:block` / `lg:block` cell visibility) so columns line up.
 */
export function UsersTableSkeleton({ rows = 8 }: UsersTableSkeletonProps) {
  return (
    <div
      data-testid="users-table-skeleton"
      className="overflow-hidden rounded-xl border border-border-muted glass-surface-subtle"
    >
      <div
        className={cn(
          "grid items-center gap-4 border-b border-border-muted px-4 py-2.5",
          "type-label-sm tracking-wider uppercase text-text-muted",
          GRID_TEMPLATE,
        )}
      >
        <span>Name</span>
        <span className="hidden sm:block">Email</span>
        <span>Status</span>
        <span className="hidden lg:block">Role</span>
        <span className="hidden lg:block">Created</span>
      </div>
      <ul className="flex flex-col">
        {Array.from({ length: rows }).map((_, i) => (
          <li
            key={i}
            className={cn(
              "grid items-center gap-4 border-t border-border-muted px-4 py-3 first:border-t-0",
              GRID_TEMPLATE,
            )}
          >
            <Skeleton shape="text" className="w-32" />
            <Skeleton shape="text" className="hidden w-40 sm:block" />
            <Skeleton shape="text" className="w-20" />
            <Skeleton shape="text" className="hidden w-16 lg:block" />
            <Skeleton shape="text" className="hidden w-24 lg:block" />
          </li>
        ))}
      </ul>
    </div>
  );
}
